import { useMemo } from "react";
import { Bot, MessageSquare, Sparkles } from "lucide-react";

import { cn } from "@/lib";
import { Button } from "@/components/ui";
import type { AllowanceShape, Metric } from "@/services/billing";

interface UsageMetricCardProps {
  metric: Metric;
  allowance: AllowanceShape;
  onBuyMore?: (metric: Metric) => void;
}

const METRIC_LABELS: Record<Metric, string> = {
  ai_conversations: "Conversas IA",
  ai_messages: "Mensagens IA",
};

const METRIC_ICONS: Record<Metric, typeof Bot> = {
  ai_conversations: Bot,
  ai_messages: MessageSquare,
};

function formatInt(value: number): string {
  return value.toLocaleString("pt-BR");
}

function formatDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const diffMs = d.getTime() - Date.now();
  if (diffMs < 0) return 0;
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
}

function progressColor(pct: number): string {
  if (pct > 100) return "bg-red-500";
  if (pct >= 80) return "bg-orange-500";
  if (pct >= 50) return "bg-yellow-500";
  return "bg-emerald-500";
}

export function UsageMetricCard({
  metric,
  allowance,
  onBuyMore,
}: UsageMetricCardProps) {
  const Icon = METRIC_ICONS[metric];
  const label = METRIC_LABELS[metric];

  const isUnlimited = allowance.plan_limit === -1;
  const pct = useMemo(() => {
    if (isUnlimited) return 0;
    if (allowance.plan_limit <= 0) return 0;
    return (allowance.plan_used / allowance.plan_limit) * 100;
  }, [allowance.plan_limit, allowance.plan_used, isUnlimited]);

  const fillPct = Math.min(100, Math.max(0, pct));
  const remaining = isUnlimited
    ? null
    : Math.max(0, allowance.plan_limit - allowance.plan_used);
  const days = daysUntil(allowance.period_end);
  const dateLabel = formatDate(allowance.period_end);

  const creditsAvailable = Math.max(
    0,
    allowance.credits_total - allowance.credits_used
  );

  return (
    <article
      data-metric={metric}
      className="flex flex-col rounded-2xl border border-border bg-white p-6 shadow-sm"
    >
      <header className="mb-4 flex items-center gap-3">
        <span className="rounded-lg bg-teal-50 p-2 text-teal-600">
          <Icon className="h-5 w-5" />
        </span>
        <h3 className="text-lg font-semibold text-foreground">{label}</h3>
      </header>

      <div className="mb-2 flex items-baseline justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          {isUnlimited ? (
            <span className="font-medium text-foreground">Ilimitado</span>
          ) : (
            <>
              <span className="font-semibold text-foreground">
                {formatInt(allowance.plan_used)}
              </span>{" "}
              / {formatInt(allowance.plan_limit)} usados
            </>
          )}
        </p>
        {!isUnlimited && remaining !== null && (
          <p className="text-xs text-muted-foreground">
            {formatInt(remaining)} restantes
          </p>
        )}
      </div>

      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(fillPct)}
        aria-label={`Uso de ${label}`}
        className="mb-4 h-2 w-full overflow-hidden rounded-full bg-zinc-100"
      >
        <div
          data-fill
          className={cn(
            "h-full rounded-full transition-all",
            isUnlimited ? "bg-emerald-500" : progressColor(pct)
          )}
          style={{ width: `${isUnlimited ? 100 : fillPct}%` }}
        />
      </div>

      <p className="mb-1 text-sm text-muted-foreground">
        {days === null ? (
          "Reset automático na renovação"
        ) : (
          <>
            Restam{" "}
            <span className="font-medium text-foreground">{days}</span>{" "}
            {days === 1 ? "dia" : "dias"} até o reset
            {dateLabel ? ` (${dateLabel})` : ""}
          </>
        )}
      </p>

      <p className="mb-4 flex items-center gap-1.5 text-sm text-muted-foreground">
        <Sparkles className="h-3.5 w-3.5 text-teal-500" />
        Créditos extras:{" "}
        <span className="font-medium text-foreground">
          {formatInt(creditsAvailable)} disponíveis
        </span>
        <span className="text-xs">
          ({formatInt(allowance.credits_used)} usados)
        </span>
      </p>

      <Button
        type="button"
        variant="outline"
        className="mt-auto w-full"
        onClick={() => onBuyMore?.(metric)}
      >
        Comprar mais
      </Button>
    </article>
  );
}
